import React from 'react';
import { MenuRow, MenuSurface } from '~/design-system/components/v1_5/Header';
import { DIRECTORY_MENU_WIDTH } from './KindMenu';
import type { DirectoryKind, DirectoryRowAction, HeaderTheme } from './theme';
import { useV15Theme } from '~/design-system/components/v1_5/theme-context';

/**
 * One registry, every action. No divider (D10) — delete is already marked as the dangerous one by
 * being red and by being last in every `KIND_ROW_ACTIONS` list. No shortcut hints either: no key is
 * bound to any of these, and printing ⌫ beside a Delete that does not answer it is the version of
 * that mistake with a destructive payload.
 *
 * `rename` sits right after `open` everywhere: it is the only place the double-click gesture is
 * discoverable, and the only way to reach it without a pointer. `edit` is the same glyph the deck
 * menu's own Rename row uses one bar up.
 */
const ACTION: Record<DirectoryRowAction, { icon: string; label: string; destructive?: boolean }> = {
  open: { icon: 'eye', label: 'Open' },
  rename: { icon: 'edit', label: 'Rename' },
  duplicate: { icon: 'Copy', label: 'Duplicate' },
  download: { icon: 'arrow-down', label: 'Download' },
  delete: { icon: 'delete-bin-6', label: 'Delete', destructive: true },
  'extract-text': { icon: 'text', label: 'Extract text' },
  'extract-images': { icon: 'image', label: 'Extract images' },
  'extract-styles': { icon: 'palette', label: 'Extract styles' },
  'copy-url': { icon: 'link-extract', label: 'Copy URL' },
  refetch: { icon: 'loop-right', label: 'Re-fetch' },
  present: { icon: 'play', label: 'Present' },
  'undo-edit': { icon: 'loop-right', label: 'Undo last edit' },
  'artifact-theme': { icon: 'palette', label: 'Artifact theme' },
  // No pin glyph in the registry; the layout mark says what it becomes: the kit's reference for this structure.
  'pin-reference': { icon: 'layout-view', label: 'Pin as reference' },
  'add-to-artifact': { icon: 'image-place', label: 'Add to artifact' },
};

/**
 * Where the generic word is not the honest one. An artifact downloads as the document it is, a
 * document as markdown; deleting a document is not deleting a card — it takes every instance and
 * the Library original with it, and the row says so before the click.
 */
const LABEL_BY_KIND: Partial<Record<DirectoryKind, Partial<Record<DirectoryRowAction, string>>>> = {
  artifacts: { download: 'Download .html' },
  // `Delete`, not `Delete document` — the double-confirm modal carries the every-instance warning
  // now, so the row does not have to (Guri, 2026-08-30).
  documents: { download: 'Download .md' },
};

/**
 * Actions whose result is shown IN the row: press Copy URL and the row itself says `Copied` for a
 * beat, then the menu closes — feedback lives in the control, never in a snackbar (Guri,
 * 2026-08-30). Exported so both hosts (the panel and the preview bar) skip their immediate close
 * for exactly these and close on `onSettled` instead.
 */
export const RESULT_FEEDBACK: Partial<Record<DirectoryRowAction, string>> = {
  'copy-url': 'Copied',
  // `pin-reference` is deliberately absent: it resolves the kit and may ask first, so its result is
  // the row's own flash ("Pinned to <kit>") once the write has actually landed.
};

/** How long the result label holds before the menu excuses itself. */
const RESULT_HOLD_MS = 900;

export interface RowMenuProps {
  onAction?: (action: DirectoryRowAction) => void;
  /** Fired after a RESULT_FEEDBACK action has shown its result — the host closes the menu here. */
  onSettled?: (action: DirectoryRowAction) => void;
  /**
   * The rows to draw, in order. The caller passes the item's real set — `KIND_ROW_ACTIONS[kind]`
   * minus whatever this item cannot do — and every row it leaves in must be wired (Header rule 5).
   * Absent, the menu draws the base four so old callers and stories keep meaning what they meant.
   */
  rows?: DirectoryRowAction[];
  /** Sharpens generic labels per kind (`Download .md`, `Delete document`). Absent = generic words. */
  kind?: DirectoryKind;
  /**
   * Whether this item's name can be changed. False drops the Rename row whatever `rows` says — an
   * in-flight item's name is still being written by the thing that is writing the item.
   */
  renameable?: boolean;
  theme?: HeaderTheme;
  className?: string;
}

const DEFAULT_ROWS: DirectoryRowAction[] = ['rename', 'duplicate', 'download', 'delete'];

/** The `⋯` menu, off one row. Same 196px shell as the kind and add menus — one family, one width. */
export function RowMenu({
  onAction,
  onSettled,
  rows = DEFAULT_ROWS,
  kind,
  renameable = true,
  theme: themeProp,
  className,
}: RowMenuProps) {
  const theme = useV15Theme(themeProp);
  const [settled, setSettled] = React.useState<DirectoryRowAction | null>(null);
  const timer = React.useRef<number | undefined>(undefined);
  React.useEffect(() => () => window.clearTimeout(timer.current), []);

  const drawn = renameable ? rows : rows.filter((id) => id !== 'rename');
  return (
    <MenuSurface width={DIRECTORY_MENU_WIDTH} theme={theme} label="Item" className={className}>
      {drawn.map((id) => (
        <MenuRow
          key={id}
          icon={ACTION[id].icon}
          label={settled === id ? RESULT_FEEDBACK[id] ?? ACTION[id].label : (kind && LABEL_BY_KIND[kind]?.[id]) ?? ACTION[id].label}
          // Red comes from `MenuRow`'s own `destructive`, never from a colour written here — a
          // bespoke red is how two deletes in one app end up two different reds.
          destructive={ACTION[id].destructive}
          theme={theme}
          onClick={() => {
            if (settled !== null) return; // the menu is on its way out; a second press means nothing
            onAction?.(id);
            const feedback = RESULT_FEEDBACK[id];
            if (feedback === undefined || !onSettled) return;
            // The action ran; the row wears its result while the menu stays for one beat.
            setSettled(id);
            timer.current = window.setTimeout(() => onSettled(id), RESULT_HOLD_MS);
          }}
        />
      ))}
    </MenuSurface>
  );
}

/**
 * What an N-row `RowMenu` measures, so the panel can keep it on screen without rendering it first.
 *
 * N 36px rows, N-1 2px gaps, the surface's 5px padding twice. A function rather than a measurement
 * because the panel has to decide where to put the menu on the same frame the pointer asked for it —
 * a measure-then-place would put the menu in the wrong spot for one frame, which is exactly the
 * frame the eye is on.
 */
export function rowMenuHeight(count: number): number {
  return count * 36 + Math.max(0, count - 1) * 2 + 2 * 5;
}

/** The default four-row height, derived so adding a row cannot leave it describing the old menu. */
export const ROW_MENU_HEIGHT = rowMenuHeight(DEFAULT_ROWS.length);

/** The same menu one row shorter, for the kinds whose names are not theirs to change. */
export const ROW_MENU_HEIGHT_NO_RENAME = rowMenuHeight(DEFAULT_ROWS.length - 1);
